import React, { useState, useRef } from 'react';
import {fetchFormData} from "./api";

const FileUploadAndProcess = () => {
    const [file, setFile] = useState(null); // To store the selected file
    const [errorMessage, setErrorMessage] = useState(''); // To store validation messages
    const [successMessage, setSuccessMessage] = useState(''); // To store success messages
    const [excelData, setExcelData] = useState([]); // To store the extracted Excel data
    const [loading, setLoading] = useState(false); // To manage the loading state
    const [paymentMode, setPaymentMode] = useState(6); // To store selected payment mode
    const maxSize = 5 * 1024 * 1024; // 5MB size limit
    const fileInputRef = useRef(null); // Create a ref for the file input

    // Handle file selection
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];

        if (selectedFile) {
            // Check file type (xlsx or csv)
            const validTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
                'text/csv', // .csv
            ];

            if (!validTypes.includes(selectedFile.type)) {
                setErrorMessage('Please upload a .xlsx or .csv file.');
                setFile(null); // Reset file state
                return;
            }

            // Check file size (< 5MB)
            if (selectedFile.size > maxSize) {
                setErrorMessage('File size should be below 5MB.');
                setFile(null); // Reset file state
                return;
            }

            // If both checks pass, set the file
            setFile(selectedFile);
            setErrorMessage('');
        }
    };

    // Handle form submission (file upload)
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file) {
            setErrorMessage('No file selected.');
            return;
        }

        // Prepare form data to send the file
        const formData = new FormData();
        formData.append('file', file);
        formData.append('uploadMode', paymentMode); // Append the selected payment mode

        // Start loading and disable interaction
        setLoading(true);

        try {
            // Send the file to the backend (Node.js server) using axios
            const response = await fetchFormData('/pdf/employee/promotion', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Handle success response
            if (response.success) {
                setSuccessMessage('File uploaded and processed successfully!');
                setExcelData(response.data);
                // Reset the file input
                setFile(null); // Reset file state

                // Reset the input field
                if (fileInputRef.current) {
                    fileInputRef.current.value = null; // Clear the input value
                }
            } else {
                setErrorMessage('File upload failed.');
            }
        } catch (error) {
            console.error('Error uploading the file:', error);
            setErrorMessage('An error occurred while uploading the file.');
        } finally {
            // Stop loading and re-enable interaction
            setLoading(false);
        }

        setTimeout(() => {
            setErrorMessage(''); // Clear error message
            setSuccessMessage(''); // Clear success message
        }, 5000);
    };

    return (
        <div className="container mt-5">
            <h2 className="mb-4">Employee promotion .xlsx or .csv File (Max Size: 5MB)</h2>
            <p>Excel should have the following fields in this pattern: <span className="fw-bold text-danger">employee_id, name, phone_number</span></p>
            <div className="row">
                {/* 1st Column - File Upload */}
                <div className="col-6">
                    <div
                        style={{
                            pointerEvents: loading ? 'none' : 'auto',
                            opacity: loading ? 0.5 : 1,
                            position: 'relative',
                        }}
                    >
                        {/* Payment Mode Selection */}
                        <div className="mb-3">
                            <label>Payment Mode:</label>
                            <div className="form-check">
                                <input
                                    type="radio"
                                    id="credit"
                                    name="paymentMode"
                                    value="6"
                                    className="form-check-input"
                                    checked={paymentMode === 6}
                                    onChange={() => setPaymentMode(6)}
                                />
                                <label className="form-check-label" htmlFor="credit">Razor pay</label>
                            </div>
                            <div className="form-check">
                                <input
                                    type="radio"
                                    id="debit"
                                    name="paymentMode"
                                    value="1"
                                    className="form-check-input"
                                    checked={paymentMode === 1}
                                    onChange={() => setPaymentMode(1)}
                                />
                                <label className="form-check-label" htmlFor="debit">G pay</label>
                            </div>
                            <div className="form-check">
                                <input
                                    type="radio"
                                    id="paypal"
                                    name="paymentMode"
                                    value="2"
                                    className="form-check-input"
                                    checked={paymentMode === 2}
                                    onChange={() => setPaymentMode(2)}
                                />
                                <label className="form-check-label" htmlFor="paypal">Phonepe</label>
                            </div>
                        </div>

                        {/* File input */}
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <input
                                    type="file"
                                    accept=".xlsx, .csv"
                                    className="form-control"
                                    onChange={handleFileChange}
                                    ref={fileInputRef} // Attach ref here
                                />
                            </div>

                            {/* Display error and success messages */}
                            {errorMessage && <p className="text-danger">{errorMessage}</p>}
                            {successMessage && <p className="text-success">{successMessage}</p>}

                            <button type="submit" className="btn btn-primary" disabled={!file || loading}>
                                {loading ? 'Uploading...' : 'Upload and Process'}
                            </button>
                        </form>

                        {/* Loader during the upload process */}
                        {loading && (
                            <div style={loaderStyle}>
                                <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 2nd Column - Table View */}
                <div className="col-6">
                    {/* Display Excel Data */}
                    {excelData.length > 0 && (
                        <div className="mt-4">
                            <h3>Excel Data</h3>
                            <div style={tableWrapperStyle}>
                                <table className="table table-bordered table-striped">
                                    <thead className="thead-dark">
                                    <tr>
                                        {Object.keys(excelData[0]).map((key) => (
                                            <th key={key}>{key}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {excelData.map((row, index) => (
                                        <tr key={index}>
                                            {Object.values(row).map((value, i) => (
                                                <td key={i}>{value}</td>
                                            ))}
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const loaderStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1,
    background: 'rgba(255, 255, 255, 0.8)',
    padding: '10px 20px',
    borderRadius: '5px',
    fontSize: '16px',
    color: '#333',
};

const tableWrapperStyle = {
    maxHeight: '400px',
    overflowX: 'auto',
    overflowY: 'auto',
    whiteSpace: 'nowrap',
};

export default FileUploadAndProcess;
